// Helpers for matching portal item selections against the staff member's
// existing draft order lines (the "basket"). BC overwrites the quantity of a
// matching variant line, so the portal needs to know when one already exists.

const norm = (value) => (value ?? "").toString().trim().toUpperCase();

const byLineNo = (a, b) => Number(a.lineNo) - Number(b.lineNo);

const sameVariant = (line, { productCode, colour, size, fit }) =>
  norm(line.productCode) === norm(productCode) &&
  norm(line.colour) === norm(colour) &&
  norm(line.size) === norm(size) &&
  norm(line.fit) === norm(fit);

const isFilled = (line) => !!line.itemNo;

/**
 * Existing basket line for the exact variant. When several match (legacy
 * duplicates) the lowest line number is returned, mirroring BC's FindFirst.
 */
export const findExistingVariantLine = (lines = [], variant) =>
  lines
    .filter((line) => isFilled(line) && sameVariant(line, variant))
    .sort(byLineNo)[0] ?? null;

/**
 * Line number BC will update for this variant: the matching variant line,
 * otherwise the product's blank placeholder line, otherwise the fallback.
 */
export const resolveBackendLineNo = (lines = [], variant, fallback) => {
  const existing = findExistingVariantLine(lines, variant);
  if (existing) return existing.lineNo;
  const blank = lines
    .filter(
      (line) =>
        !isFilled(line) && norm(line.productCode) === norm(variant.productCode),
    )
    .sort(byLineNo)[0];
  return blank?.lineNo ?? fallback;
};

/**
 * Save lines whose variant is already in the basket with a quantity, paired
 * with that existing basket line.
 */
export const findBasketConflicts = (lines = [], saveLines = []) =>
  saveLines
    .map((saveLine) => ({
      saveLine,
      existing: findExistingVariantLine(lines, saveLine),
    }))
    .filter(({ existing }) => existing && Number(existing.quantity) > 0);

/**
 * Apply the user's choice to the save lines. "add" sums the basket quantity
 * into each conflicting line; "replace" leaves the entered quantity as is.
 */
export const applyBasketChoice = (saveLines, conflicts, choice) => {
  if (choice !== "add") return saveLines;
  return saveLines.map((saveLine) => {
    const conflict = conflicts.find((c) => c.saveLine === saveLine);
    if (!conflict) return saveLine;
    return {
      ...saveLine,
      quantity: Number(saveLine.quantity) + Number(conflict.existing.quantity),
    };
  });
};
